import { useCallback, useEffect, useRef, useState } from 'react';
import { dataCache } from '../services/cache/dataCache';

interface UseCachedQueryOptions {
  ttlMs: number;
  enabled?: boolean;
}

interface UseCachedQueryResult<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: (force?: boolean) => Promise<T | null>;
  isStale: boolean;
}

export function useCachedQuery<T>(
  key: string,
  fetcher: () => Promise<T>,
  options: UseCachedQueryOptions
): UseCachedQueryResult<T> {
  const { ttlMs, enabled = true } = options;
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const [data, setData] = useState<T | null>(() => dataCache.get<T>(key, ttlMs));
  const [loading, setLoading] = useState(() => !dataCache.get<T>(key, ttlMs));
  const [error, setError] = useState<Error | null>(null);
  const [isStale, setIsStale] = useState(false);

  const refetch = useCallback(async (force = false): Promise<T | null> => {
    if (!enabled) return null;

    if (!force) {
      const cached = dataCache.get<T>(key, ttlMs);
      if (cached !== null) {
        setData(cached);
        setLoading(false);
        setError(null);
        setIsStale(false);
        return cached;
      }
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetcherRef.current();
      dataCache.set(key, result);
      setData(result);
      setIsStale(false);
      return result;
    } catch (err) {
      const fetchError = err instanceof Error ? err : new Error('Veri yüklenemedi');
      setError(fetchError);
      return null;
    } finally {
      setLoading(false);
    }
  }, [key, ttlMs, enabled]);

  useEffect(() => {
    if (!enabled) return;

    const cached = dataCache.get<T>(key, ttlMs);
    if (cached !== null) {
      setData(cached);
      setLoading(false);
      setIsStale(false);
      return;
    }

    refetch(true);
  }, [key, ttlMs, enabled, refetch]);

  return { data, loading, error, refetch, isStale };
}
